import { z } from 'zod';
import { BaseApi } from '@/services/api/base-api.ts';
import zodApprovalApplicationAddDto from '@/schemas/approval/zod-ApprovalApplicationAddDto.js';
import zodApprovalApplicationWithEmployeeDocumentLineDto from '@/schemas/approval/zod-ApprovalApplicationWithEmployeeDocumentLineDto.js';
import zodApprovalApplicationWithDocumentDto from '@/schemas/approval/zod-ApprovalApplicationWithDocumentDto.js';
import zodApprovalApplicationWithManagerDocumentLineDto
  from "@/schemas/approval/zod-ApprovalApplicationWithManagerDocumentLineDto";

export type ApprovalApplicationRequest = z.infer<
  typeof zodApprovalApplicationAddDto
>;

export type ApprovalApplicationDto = z.infer<
  typeof zodApprovalApplicationWithDocumentDto
>;

const ApprovalApplicationDtoList = z.array(
  zodApprovalApplicationWithDocumentDto
);

export type ApprovalApplicationWithManagerDocumentLineDto = z.infer<
  typeof zodApprovalApplicationWithManagerDocumentLineDto
>;

export type ApprovalApplicationWithEmployeeDocumentLineDto = z.infer<
  typeof zodApprovalApplicationWithEmployeeDocumentLineDto
>;

const ApprovalApplicationWithEmployeeDocumentLineDtoList = z.array(
  zodApprovalApplicationWithEmployeeDocumentLineDto
);

export class ApprovalApi extends BaseApi {
  constructor() {
    super();
  }

  async listByMeAsync(startDt: string, endDt: string) {
    return this.requestAsync({
      schema: ApprovalApplicationDtoList,
      url: 'approval/applications',
      query: {
        startDate: startDt,
        endDate: endDt,
      },
    });
  }

  async getAsync(applicationId: number) {
    return this.requestAsync({
      schema: zodApprovalApplicationWithManagerDocumentLineDto,
      url: `approval/application/${applicationId}`,
    });
  }

  async createAsync(
    applyType: string,
    application: ApprovalApplicationRequest
  ) {
    return this.requestAsync({
      schema: z.boolean(),
      method: 'post',
      url: `approval/application/${applyType}`,
      payload: application,
    });
  }

  async deleteAsync(applicationId: number, comment: string) {
    const params = {
      comment: comment,
    };

    return this.requestAsync({
      schema: z.boolean(),
      method: 'delete',
      url: `approval/application/${applicationId}`,
      query: params,
    });
  }

  async listByPendingAsync() {
    return this.requestAsync({
      schema: ApprovalApplicationWithEmployeeDocumentLineDtoList,
      url: 'approval/applications/pending',
    });
  }

  async listByConfirmAsync(startDt: string, endDt: string) {
    return this.requestAsync({
      schema: ApprovalApplicationWithEmployeeDocumentLineDtoList,
      url: 'approval/applications/confirmed',
      query: {
        startDate: startDt,
        endDate: endDt,
      },
    });
  }

  async agreeAsync(approvalLineId: number, comment: string) {
    return this.requestAsync({
      schema: z.boolean(),
      method: 'post',
      url: `approval/application/line/${approvalLineId}/agree`,
      query: {
        comment: comment,
      },
    });
  }

  async disagreeAsync(approvalLineId: number, comment: string) {
    return this.requestAsync({
      schema: z.boolean(),
      method: 'post',
      url: `approval/application/line/${approvalLineId}/disagree`,
      query: {
        comment: comment,
      },
    });
  }
}
